import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Typography,
} from '@mui/material';

import { TripPurpose } from '@/data/models/trip-purpose.model';
import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useAppSelector } from '@/hooks/useAppSelector';
import * as clientSlice from '@/data/client.slice';
import type { TripArrayFieldContext } from '../machines/createTripArrayFieldMachine';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import { MEMBER_PORTAL_EXCLUDED_PURPOSE_NAMES } from '@/data/constants';
import { usePortalContext } from '@/contexts/PortalProvider';

const PREFIX = 'ReasonForTripField';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
}));

export function ReasonForTripField({ index }: { index: number }) {
  const { t } = useTranslation('common');
  const { control, trigger } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const { isMemberPortal } = usePortalContext();
  const actor = fieldRefs.reasonForTrip;
  const snapshot = useFieldActorSnapshot(actor);
  const values = (snapshot.context as TripArrayFieldContext).values;
  const raw = values[index];
  const value = typeof raw === 'string' ? raw : '';

  const passengerClientData = useAppSelector(
    clientSlice.selectPassengerClientData,
  );

  const sortedTripPurposes = useMemo(() => {
    let purposeCodes = passengerClientData?.purposeCodes ?? [];
    if (isMemberPortal) {
      purposeCodes = purposeCodes.filter((p) => !MEMBER_PORTAL_EXCLUDED_PURPOSE_NAMES.includes(p.name?.toLowerCase() || ''));
    }
    const sortedPurposeCodes = [...purposeCodes].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    return sortedPurposeCodes;
  }, [passengerClientData.purposeCodes, isMemberPortal]);

  const labelId = `reason-for-trip-label-${index}`;

  return (
    <StyledBox className={classes.container}>
      <Controller
        name={`trips.${index}.reasonForTrip`}
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth required error={Boolean(fieldState.error)}>
            <InputLabel id={labelId} required>
              {t('member.reasonForTrip')}
            </InputLabel>
            <Select
              labelId={labelId}
              label={t('member.reasonForTrip')}
              value={value}
              onChange={(e) => {
                const v = e.target.value;
                actor.send({ type: 'CHANGE', index, value: v });
                field.onChange(v);
                // Re-validate just this field so its surfaced required error
                // clears on selection. Under `mode: 'onBlur'` RHF skips
                // change-time validation before submit, so without this the
                // error lingers until the dropdown is blurred. Scoped to the
                // field itself so it doesn't surface errors on other fields or
                // trip legs.
                trigger(`trips.${index}.reasonForTrip`);
              }}
              onBlur={() => {
                actor.send({ type: 'BLUR', index });
                field.onBlur();
              }}
              onFocus={() => actor.send({ type: 'FOCUS', index })}
              name={field.name}
              inputRef={field.ref}
              displayEmpty
              required
              renderValue={(selected) => {
                if (!selected) {
                  return '';
                }
                const purpose = sortedTripPurposes.find(
                  (p) => p.purposeCodeUUID === selected,
                );
                return purpose?.name ?? '';
              }}>
              {sortedTripPurposes.map((purpose) => (
                <MenuItem
                  key={purpose.purposeCodeUUID}
                  value={purpose.purposeCodeUUID}>
                  {purpose.name}
                </MenuItem>
              ))}
            </Select>
            {fieldState.error?.message && (
              <FormHelperText>{fieldState.error.message}</FormHelperText>
            )}
          </FormControl>
        )}
      />
    </StyledBox>
  );
}
